import React, { useCallback } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';

import { useFormikContext } from 'formik';

import { updateQueryStringOnFieldChange } from '@components/Filters/Filters';
import { useThrottle } from '@features/useThrottle';
import { areaApi, countryApi } from '@reducers/geo';
import { reportsApi } from '@reducers/reports/reports';
import { skipToken } from '@reduxjs/toolkit/query';

export const CdrsReportFilter = ({ searchParams, setSearchParams }) => {
  const { handleChange, handleBlur, values } = useFormikContext();
  const countries = countryApi.useGetForSelectQuery();
  const areas = areaApi.useGetForSelectQuery(
    values.country ? { id: values.country } : skipToken
  );
  const destinations = countryApi.useGetDestinationsForSelectQuery(
    values.country ? { id: values.country } : skipToken
  );
  const trafficTypes = reportsApi.useGetTrafficTypesQuery();

  const throttle = useThrottle((event) => {
    updateQueryStringOnFieldChange(event, { searchParams, setSearchParams });
  }, 500);

  const throttledChangeHandler = useCallback(
    (event) => {
      handleChange(event);
      throttle(event);
    },
    [throttle]
  );

  return (
    <Card>
      <Card.Header className="bg-didxl text-white">Search form</Card.Header>
      <Card.Body>
        <Form.Group className="mb-3" controlId="formGroupCountry">
          <Form.Label>Country</Form.Label>
          <Form.Select
            name="country"
            size="sm"
            value={values.country}
            onChange={(e) => {
              handleChange(e);
              updateQueryStringOnFieldChange(e, {
                searchParams,
                setSearchParams,
                clearFields: ['area', 'destination'],
              });
            }}
            onBlur={handleBlur}
          >
            <option value="">Select country</option>
            {!countries.isLoading &&
            !countries.isError &&
            countries.data.length > 0 ? (
              <>
                {countries.data.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.name}
                  </option>
                ))}
              </>
            ) : null}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="formGroupArea">
          <Form.Label>Area</Form.Label>
          <Form.Select
            name="area"
            size="sm"
            value={values.area}
            onChange={(e) => {
              handleChange(e);
              updateQueryStringOnFieldChange(e, {
                searchParams,
                setSearchParams,
              });
            }}
            onBlur={handleBlur}
            disabled={!values.country}
          >
            <option value="">
              {values.country ? 'Select area' : 'Select country to see areas'}
            </option>
            {!areas.isUninitialized &&
            !areas.isLoading &&
            !areas.isError &&
            areas.data.length > 0 ? (
              <>
                {areas.data.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.name}
                  </option>
                ))}
              </>
            ) : null}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="formGroupStartDate">
          <Form.Label>From</Form.Label>
          <Row>
            <Col md={7}>
              <Form.Control
                name="startDate"
                type="date"
                size="sm"
                value={values.startDate}
                onChange={throttledChangeHandler}
                onBlur={handleBlur}
              />
            </Col>
            <Col md={5}>
              <Form.Control
                name="startDateTime"
                type="time"
                size="sm"
                value={values.startDateTime}
                onChange={throttledChangeHandler}
                onBlur={handleBlur}
              />
            </Col>
          </Row>
        </Form.Group>
        <Form.Group className="mb-3" controlId="formGroupEndDate">
          <Form.Label>To</Form.Label>
          <Row>
            <Col md={7}>
              <Form.Control
                name="endDate"
                type="date"
                size="sm"
                value={values.endDate}
                onChange={throttledChangeHandler}
                onBlur={handleBlur}
              />
            </Col>
            <Col md={5}>
              <Form.Control
                name="endDateTime"
                type="time"
                size="sm"
                value={values.endDateTime}
                onChange={throttledChangeHandler}
                onBlur={handleBlur}
              />
            </Col>
          </Row>
        </Form.Group>
        <Form.Group className="mb-3" controlId="formGroupCompleted">
          <Form.Label>Completed</Form.Label>
          <Form.Select
            name="completed"
            size="sm"
            value={values.completed}
            onChange={(e) => {
              handleChange(e);
              updateQueryStringOnFieldChange(e, {
                searchParams,
                setSearchParams,
              });
            }}
            onBlur={handleBlur}
          >
            <option value="">-</option>
            <option value={false}>No</option>
            <option value>Yes</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="formGroupTrafficType">
          <Form.Label>Traffic type</Form.Label>
          <Form.Select
            name="trafficType"
            size="sm"
            value={values.trafficType}
            onChange={(e) => {
              handleChange(e);
              updateQueryStringOnFieldChange(e, {
                searchParams,
                setSearchParams,
              });
            }}
            onBlur={handleBlur}
            disabled={trafficTypes.isLoading || trafficTypes.isFetching}
          >
            <option value="">-</option>
            {!trafficTypes.isUninitialized &&
            !trafficTypes.isLoading &&
            !trafficTypes.isError &&
            trafficTypes.data.length > 0 ? (
              <>
                {trafficTypes.data.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.name}
                  </option>
                ))}
              </>
            ) : null}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="formGroupDestination">
          <Form.Label>Destination</Form.Label>
          <Form.Select
            name="destination"
            size="sm"
            value={values.destination}
            onChange={(e) => {
              handleChange(e);
              updateQueryStringOnFieldChange(e, {
                searchParams,
                setSearchParams,
              });
            }}
            onBlur={handleBlur}
            disabled={
              !values.country ||
              destinations.isLoading ||
              destinations.isFetching
            }
          >
            <option value="">
              {values.country
                ? 'Select destination'
                : 'Select country to see destinations'}
            </option>
            {!destinations.isUninitialized &&
            !destinations.isLoading &&
            !destinations.isError &&
            destinations.data.length > 0 ? (
              <>
                {destinations.data.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.name}
                  </option>
                ))}
              </>
            ) : null}
          </Form.Select>
        </Form.Group>
      </Card.Body>
    </Card>
  );
};
